import { describeApiError, type PointsSystem, type PointsSystemVersion } from '@brush/api-client';
import {
  ErrorNotice,
  ErrorRetry,
  PointsSystemBuilder,
  Spinner,
  VersionStatusPill,
  blankScope,
  breakdownState,
  compareState,
  comparisonBaseline,
  controlClass,
  definitionKey,
  ghostButtonClass,
  primaryButtonClass,
  readDefinition,
  readScope,
  scopeEventsFrom,
  scopeKey,
  tagVocabularyFromUsage,
  toDefinitionDocument,
  toPreviewState,
  toScopeDocument,
  unsavedWorkLabel,
  useDebouncedValue,
  validateDefinition,
  validateScope,
  type PointsBreakdownAccess,
  type PointsCompare,
  type PointsDefinition,
  type PointsScope,
} from '@brush/ui';
import { useEffect, useMemo, useState } from 'react';
import {
  useCreatePointsSystemVersion,
  useDeletePointsSystemVersion,
  usePatchPointsSystem,
  usePlatformTags,
  usePointsPreview,
  usePointsPreviewBreakdown,
  usePointsSystemVersion,
  usePublishPointsSystemVersion,
  useRoomEvents,
  useRoomTagUsage,
  useRooms,
  useSavePointsSystemVersion,
} from '../../queries';
import { Dialog } from './Dialog';

/**
 * The designer, for a platform system.
 *
 * <b>The preview needs a room, and a platform system doesn't have one.</b> That is the one thing
 * this screen has to solve that the room console doesn't: the endpoint scores a room's real
 * results, so an administrator picks which room to try the system against. Changing it never
 * touches the definition — a system authored here is scored by whichever room adopts it, and the
 * selector is a test harness, not a setting.
 *
 * Everything else is the shared builder. This file owns the draft, the save, and §9.6's publish.
 */
export function PointsSystemBuilderScreen({
  system,
  onBack,
}: {
  system: PointsSystem;
  onBack: () => void;
}) {
  const versions = [...system.versions].sort((a, b) => b.versionNumber - a.versionNumber);
  const newest = versions[0]?.versionNumber ?? null;
  const [versionNumber, setVersionNumber] = useState<number | null>(newest);

  // Only ever fills in a missing selection — a system that had no versions when this opened and
  // has one now. It deliberately does *not* follow the newest version: an administrator who has
  // selected v1 to read it would otherwise be snapped back to v3 the moment the list refetched.
  // Creating a version from here sets the selection itself.
  useEffect(() => {
    if (versionNumber === null && newest !== null) setVersionNumber(newest);
  }, [newest, versionNumber]);

  const version = usePointsSystemVersion(system.id, versionNumber);
  const createVersion = useCreatePointsSystemVersion();

  if (versionNumber === null) {
    return (
      <EmptySystem
        system={system}
        onBack={onBack}
        pending={createVersion.isPending}
        error={describeApiError(createVersion.error)}
        onCreate={() =>
          createVersion.mutate(
            { id: system.id, copyFromVersion: null },
            { onSuccess: (created) => setVersionNumber(created.versionNumber) },
          )
        }
      />
    );
  }

  return (
    <div>
      <Header
        system={system}
        versions={versions}
        current={versionNumber}
        onSelect={setVersionNumber}
        onBack={onBack}
      />

      {version.isLoading ? (
        <Spinner label="Loading the definition…" />
      ) : version.isError || !version.data ? (
        <ErrorRetry
          message="Couldn't load that version."
          onRetry={() => void version.refetch()}
        />
      ) : (
        <Editor
          // Remounted per version, so switching between a published version and a draft never
          // carries one's edits into the other.
          key={`${system.id}:${versionNumber}`}
          system={system}
          version={version.data}
          onVersionCreated={setVersionNumber}
          onVersionDiscarded={onBack}
        />
      )}
    </div>
  );
}

function Header({
  system,
  versions,
  current,
  onSelect,
  onBack,
}: {
  system: PointsSystem;
  versions: PointsSystem['versions'];
  current: number;
  onSelect: (n: number) => void;
  onBack: () => void;
}) {
  // A version created a moment ago is being edited before the catalogue list has refetched, so
  // the selected number can legitimately be absent from `versions`. Without this the select has
  // no matching option and renders blank — which reads as having lost the new draft.
  const known = versions.map((v) => ({ versionNumber: v.versionNumber, status: v.status }));
  const options = known.some((v) => v.versionNumber === current)
    ? known
    : [{ versionNumber: current, status: 'draft' }, ...known];
  const status = options.find((v) => v.versionNumber === current)?.status ?? 'draft';

  return (
    <div className="mb-5">
      <button
        type="button"
        onClick={onBack}
        className="mb-3 text-xs font-medium text-ink-800/60 underline underline-offset-2 hover:text-ink-950"
      >
        ← All points systems
      </button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex flex-wrap items-center gap-2 font-display text-xl font-semibold text-ink-950">
          {system.name}
          {/* §9.6's draft badge, beside the name rather than buried in the version list: it is
              the answer to "can I change this", which is the first thing an operator wants. */}
          <VersionStatusPill status={status} />
        </h2>
        <div className="flex items-center gap-2">
          <label className="text-[11px] uppercase tracking-[0.08em] text-ink-800/45" htmlFor="version">
            Version
          </label>
          <select
            id="version"
            value={current}
            onChange={(e) => onSelect(Number(e.target.value))}
            className={controlClass}
          >
            {options.map((v) => (
              <option key={v.versionNumber} value={v.versionNumber}>
                v{v.versionNumber} — {v.status}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

/** A catalogue entry with nothing in it yet: no picker offers it, because there is nothing to pin. */
function EmptySystem({
  system,
  onBack,
  onCreate,
  pending,
  error,
}: {
  system: PointsSystem;
  onBack: () => void;
  onCreate: () => void;
  pending: boolean;
  error: string | null;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-3 text-xs font-medium text-ink-800/60 underline underline-offset-2 hover:text-ink-950"
      >
        ← All points systems
      </button>
      <div className="rounded-xl border border-ink-800/10 bg-white p-10 text-center">
        <p className="text-sm font-medium text-ink-950">{system.name} has no versions yet</p>
        <p className="mx-auto mt-1 max-w-md text-xs text-ink-800/60">
          A system is a catalogue entry; what actually scores is a version of it. Nothing can pin
          this until it has one.
        </p>
        <button type="button" onClick={onCreate} disabled={pending} className={`mt-4 ${primaryButtonClass}`}>
          {pending ? 'Creating…' : 'Start version 1'}
        </button>
        <ErrorNotice message={error} className="mt-4 text-left" />
      </div>
    </div>
  );
}

function Editor({
  system,
  version,
  onVersionCreated,
  onVersionDiscarded,
}: {
  system: PointsSystem;
  version: PointsSystemVersion;
  onVersionCreated: (n: number) => void;
  /**
   * Called once a draft has been discarded. It leaves the screen rather than picking another
   * version: the catalogue has not refetched yet, so the newest version this screen knows about
   * is the one that was just deleted, and selecting it would show a definition that no longer
   * exists as though it were still editable.
   */
  onVersionDiscarded: () => void;
}) {
  const editable = version.status === 'draft';

  const [draft, setDraft] = useState<PointsDefinition>(() => readDefinition(version.definition));

  // §9.8's test scope, held beside the draft rather than inside it — it is a separate document
  // with a separate owner (§2.1, §3.2), and the server keeps it in a separate column. A version
  // that has never been tested against anything opens on the default window.
  const [scope, setScope] = useState<PointsScope>(
    () => readScope(version.previewScope) ?? blankScope(),
  );

  // What "unchanged" means: whatever was last stored. Updated on a successful save, so the footer
  // goes quiet without anything having to remember that a save happened.
  const [baseline, setBaseline] = useState(() => definitionKey(draft));
  const [scopeBaseline, setScopeBaseline] = useState(() => scopeKey(scope));
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // The test scope counts as unsaved work: it is stored on the version, so leaving without saving
  // really does lose it. But it is tracked *separately* from the definition, and the footer says
  // which moved — a screen that answers a widened date range with "Unsaved changes" contradicts
  // everything the preview pane says about testing not touching the system. It is also not part
  // of what publishing freezes, which is why Publish is gated on the definition alone.
  //
  // And why this one is not gated on `editable` at all: §9.6's matrix leaves `PreviewScope`
  // editable at every status, so a published version's scope can still move and still needs
  // saving. Unconditional here because this console is the platform catalogue and its caller is
  // the administrator who owns it; room-web has the one case that closes it (a platform system,
  // which that room may not write to).
  const definitionDirty = editable && definitionKey(draft) !== baseline;
  const scopeDirty = scopeKey(scope) !== scopeBaseline;
  const dirty = definitionDirty || scopeDirty;
  useBeforeUnload(dirty);

  const validation = useMemo(() => validateDefinition(draft), [draft]);
  const scopeValidation = useMemo(() => validateScope(scope), [scope]);

  const save = useSavePointsSystemVersion();
  const patchSystem = usePatchPointsSystem();
  const publish = usePublishPointsSystemVersion();
  const createVersion = useCreatePointsSystemVersion();
  const deleteVersion = useDeletePointsSystemVersion();

  // ---- which room to try it against -------------------------------------------------------
  const rooms = useRooms();
  const [roomId, setRoomId] = useState<string | null>(null);
  const testRoomId = roomId ?? rooms.data?.[0]?.id ?? null;

  // The test room's whole list of events, read page by page without results, and its tag vocabulary from the
  // server's counts (known gap 108). Null until it lands, which the pickers and the diagnosis say (gap 112).
  const roomEvents = useRoomEvents(testRoomId);
  const tagUsage = useRoomTagUsage(testRoomId);
  const platformTags = usePlatformTags();

  const events = useMemo(
    () => (roomEvents.data ? scopeEventsFrom(roomEvents.data.rows) : null),
    [roomEvents.data],
  );
  const tags = useMemo(
    () => tagVocabularyFromUsage(tagUsage.data ?? [], platformTags.data ?? []),
    [tagUsage.data, platformTags.data],
  );

  // ---- the live board ---------------------------------------------------------------------
  // The two documents the preview takes, debounced together so a definition edit and a scope edit
  // cost one request rather than two. Long enough that dragging a spinner doesn't spend the
  // endpoint's per-minute allowance, short enough that the board still feels attached to what is
  // being typed.
  const request = useMemo(
    () => ({ definition: toDefinitionDocument(draft), scope: toScopeDocument(scope) }),
    [draft, scope],
  );
  const settled = useDebouncedValue(request, 800);

  const previewable = validation.isValid && scopeValidation.isValid;

  const preview = usePointsPreview({
    definition: settled.definition,
    scope: settled.scope,
    roomId: testRoomId,
    enabled: previewable,
  });

  const previewState = toPreviewState({
    blocked: !previewable,
    idleMessage:
      rooms.isLoading || roomEvents.isLoading
        ? null
        : testRoomId === null
          ? 'No rooms are registered yet, so there are no results to score. Register one under Rooms.'
          : null,
    data: preview.data,
    isFetching: preview.isFetching,
    error: preview.isError ? describeApiError(preview.error) : null,
  });

  // ---- §7.4's working, for whichever row is open -------------------------------------------
  // The list carries the rank, the name and the total; everything else about one player is here,
  // behind their own row, and is fetched only when somebody asks for it.
  const [openPlayerId, setOpenPlayerId] = useState<string | null>(null);

  const playerBreakdown = usePointsPreviewBreakdown({
    definition: settled.definition,
    scope: settled.scope,
    roomId: testRoomId,
    playerId: openPlayerId,
    enabled: previewable,
  });

  const breakdown: PointsBreakdownAccess = {
    openPlayerId,
    onOpen: setOpenPlayerId,
    state: breakdownState({
      error: playerBreakdown.isError ? describeApiError(playerBreakdown.error) : null,
      data: playerBreakdown.data,
      isFetching: playerBreakdown.isFetching,
    }),
  };

  // ---- §9.8's compare: this draft against what rooms are being scored by now ----------------
  const [comparing, setComparing] = useState(false);
  const baselineVersion = comparisonBaseline(system, version.versionNumber);

  // Only fetched once compare is switched on. The board it produces costs a second call to the
  // endpoint whose per-caller allowance an operator spends by editing, so it is not paid for by
  // anybody who has not asked the question.
  const published = usePointsSystemVersion(
    system.id,
    comparing ? (baselineVersion.version?.versionNumber ?? null) : null,
  );

  const comparePreview = usePointsPreview({
    definition: published.data?.definition ?? null,
    // The *same* debounced scope as the draft's board. Comparing two boards scored over different
    // events would report movement that is entirely the scope's doing.
    scope: settled.scope,
    roomId: testRoomId,
    enabled: comparing && previewable && published.data !== undefined,
  });

  const compare: PointsCompare = {
    baseline: baselineVersion.version
      ? { short: baselineVersion.short, long: baselineVersion.long }
      : null,
    reason: baselineVersion.reason ?? undefined,
    on: comparing,
    onToggle: setComparing,
    state: compareState({
      on: comparing,
      error:
        describeApiError(published.error) ??
        (comparePreview.isError ? describeApiError(comparePreview.error) : null),
      data: comparePreview.data,
      // Either board still moving means the two are not yet answering the same question.
      settling: preview.isFetching || comparePreview.isFetching || published.isLoading,
    }),
  };

  const submitSave = () => {
    save.mutate(
      {
        id: system.id,
        n: version.versionNumber,
        // A PUT carries the whole version, so the definition rides along even when the test scope
        // is the only thing that moved. On a frozen one it is sent back **exactly as it was read**
        // rather than as `toDefinitionDocument(draft)` — that is this build's shape of the
        // document, and a published one may carry keys this build knows nothing about. The server
        // compares and refuses a changed document (409), which is the right answer to a definition
        // this screen had quietly rewritten on the way through.
        definition: editable ? toDefinitionDocument(draft) : version.definition,
        previewScope: toScopeDocument(scope),
      },
      {
        onSuccess: () => {
          setBaseline(definitionKey(draft));
          setScopeBaseline(scopeKey(scope));
          // The catalogue row and the definition both carry a name. They are set together so the
          // list never shows one thing while the document says another. Only while the definition
          // is editable: on a frozen version the name fields are read-only, so a disagreement here
          // is one that was already stored and is not this save's to fix.
          if (editable && (draft.name.trim() !== system.name || (draft.description ?? '') !== (system.description ?? ''))) {
            patchSystem.mutate({
              id: system.id,
              name: draft.name.trim(),
              description: draft.description ?? '',
            });
          }
        },
      },
    );
  };

  const saveError = describeApiError(save.error) ?? describeApiError(publish.error) ?? describeApiError(createVersion.error) ?? describeApiError(deleteVersion.error);

  const unsaved = unsavedWorkLabel({ definition: definitionDirty, scope: scopeDirty });

  return (
    <>
      <PointsSystemBuilder
        value={draft}
        onChange={setDraft}
        validation={validation}
        scope={scope}
        onScopeChange={setScope}
        scopeValidation={scopeValidation}
        readOnly={!editable}
        // Never `!editable`: §9.6 freezes the definition and leaves the preview pane alone.
        scopeReadOnly={false}
        readOnlyNotice={
          version.status === 'published' ? (
            <>
              <strong>Version {version.versionNumber} is published, so this definition is
              frozen.</strong>{' '}
              Any leaderboard pinned to it is scored by exactly this, so it can never change. Use{' '}
              <em>Edit as new version</em> below to carry it into a fresh draft. Testing it against
              different results is still yours to do — the preview is live.
            </>
          ) : (
            <>
              <strong>Version {version.versionNumber} is archived.</strong> It is kept because a
              leaderboard may have been scored by it, and it is no longer offered to new ones. Its
              definition is frozen; you can still test it against whatever you like.
            </>
          )
        }
        tags={tags}
        events={events}
        eventsTruncated={roomEvents.data ? !roomEvents.data.complete : false}
        eventsFailed={roomEvents.isError}
        preview={previewState}
        previewHeader={
          <TestAgainst
            rooms={rooms.data ?? []}
            value={testRoomId}
            onChange={setRoomId}
            loading={rooms.isLoading}
          />
        }
        previewCompare={compare}
        previewBreakdown={breakdown}
        footer={
          <div className="sticky bottom-0 z-10 mt-5 flex flex-wrap items-center gap-3 border-t border-ink-800/10 bg-ground/95 py-4 backdrop-blur">
            <span className="mr-auto text-xs">
              {unsaved ? (
                <>
                  {/* Reachable on a frozen version too, where only the test scope can have moved. */}
                  <span className="font-medium text-gold-500">{unsaved.label}</span>
                  {unsaved.detail ? (
                    <span className="ml-1.5 text-ink-800/45">{unsaved.detail}</span>
                  ) : null}
                </>
              ) : editable ? (
                <span className="text-ink-800/45">All changes saved</span>
              ) : (
                // Which half is read-only, rather than "read-only" flat: the preview beside it is
                // not, and a footer saying otherwise is what stops anybody trying it.
                <span className="text-ink-800/45">Definition read-only</span>
              )}
            </span>

            {editable ? (
              <>
                {/* A draft hard-deletes and nothing references it (§2.4), so this needs an
                    ordinary confirmation and no more — but it does need one, since it sits
                    beside Save. */}
                {confirmDiscard ? (
                  <>
                    <span className="text-xs text-ink-800/70">Discard this draft for good?</span>
                    <button
                      type="button"
                      onClick={() => setConfirmDiscard(false)}
                      className={ghostButtonClass}
                    >
                      Keep it
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        deleteVersion.mutate(
                          { id: system.id, n: version.versionNumber },
                          { onSuccess: onVersionDiscarded },
                        )
                      }
                      disabled={deleteVersion.isPending}
                      className="border border-chip-500/40 px-4 py-2 text-sm font-medium text-chip-500 transition hover:bg-chip-500/5 disabled:opacity-40"
                    >
                      {deleteVersion.isPending ? 'Discarding…' : 'Discard'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDiscard(true)}
                    className="border border-chip-500/40 px-4 py-2 text-sm text-chip-500 transition hover:bg-chip-500/5"
                  >
                    Discard this draft
                  </button>
                )}
                <button
                  type="button"
                  onClick={submitSave}
                  disabled={!validation.isValid || !dirty || save.isPending}
                  className={ghostButtonClass}
                >
                  {save.isPending ? 'Saving…' : 'Save draft'}
                </button>
                {/* Gated on the *definition*, not on the test scope. Publishing freezes the
                    document, and the test scope is not part of it (§9.8) — refusing to publish
                    over an unsaved date range would say it was. Nor does it cost the scope edit:
                    a published version still takes one, and the dialog says so. */}
                <button
                  type="button"
                  onClick={() => setConfirmPublish(true)}
                  disabled={!validation.isValid || definitionDirty || publish.isPending}
                  title={
                    definitionDirty
                      ? 'Save your changes first — publishing freezes the definition as stored.'
                      : undefined
                  }
                  className={primaryButtonClass}
                >
                  Publish version {version.versionNumber}
                </button>
              </>
            ) : (
              <>
                {/* The one write a frozen version still takes. It sends the definition back
                    untouched and the test scope as edited — §9.6's matrix, as a button. */}
                <button
                  type="button"
                  onClick={submitSave}
                  disabled={!scopeDirty || save.isPending}
                  title="Publishing froze the definition, not what you test it against."
                  className={ghostButtonClass}
                >
                  {save.isPending ? 'Saving…' : 'Save test scope'}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    createVersion.mutate(
                      { id: system.id, copyFromVersion: version.versionNumber },
                      { onSuccess: (created) => onVersionCreated(created.versionNumber) },
                    )
                  }
                  disabled={createVersion.isPending}
                  className={primaryButtonClass}
                >
                  {createVersion.isPending ? 'Creating…' : 'Edit as new version'}
                </button>
              </>
            )}
          </div>
        }
      />

      <ErrorNotice message={saveError} className="mt-3" />

      {confirmPublish ? (
        <PublishDialog
          system={system}
          versionNumber={version.versionNumber}
          unsavedScope={scopeDirty}
          pending={publish.isPending}
          onClose={() => setConfirmPublish(false)}
          onConfirm={() =>
            publish.mutate(
              { id: system.id, n: version.versionNumber },
              { onSuccess: () => setConfirmPublish(false) },
            )
          }
        />
      ) : null}
    </>
  );
}

/**
 * §9.8's "Test against", in the one form this phase needs: which room's results to score.
 *
 * The line under it is not decoration. An operator must not fear that testing changes what they
 * are building — the whole value of a live preview is that it is free to poke at.
 */
function TestAgainst({
  rooms,
  value,
  onChange,
  loading,
}: {
  rooms: { id: string; name: string }[];
  value: string | null;
  onChange: (id: string) => void;
  loading: boolean;
}) {
  if (loading) return <p className="text-[11.5px] text-ink-800/45">Loading rooms…</p>;
  if (rooms.length === 0) return null;

  return (
    <div>
      <label className="mb-1 block text-[11px] uppercase tracking-[0.08em] text-ink-800/45" htmlFor="test-room">
        Test against
      </label>
      <select
        id="test-room"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className={`${controlClass} w-full`}
      >
        {rooms.map((room) => (
          <option key={room.id} value={room.id}>
            {room.name}
          </option>
        ))}
      </select>
      <p className="mt-1 text-[11px] text-ink-800/45">
        Whose results to score while you design. It changes nothing about the system — a room that
        adopts this is always scored on its own events.
      </p>
    </div>
  );
}

/** §9.6 — the confirmation says what publishing means, because it cannot be undone. */
function PublishDialog({
  system,
  versionNumber,
  unsavedScope,
  pending,
  onClose,
  onConfirm,
}: {
  system: PointsSystem;
  versionNumber: number;
  /**
   * The test scope has changes that were never saved. Not a blocker, and no longer a warning
   * either — the bullet it drives says so, because "will this be lost?" is the question an
   * operator has at this moment and the answer changed.
   */
  unsavedScope: boolean;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      title={`Publish version ${versionNumber}?`}
      subtitle={system.name}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-ink-800/15 py-2 text-sm text-ink-800 hover:bg-ink-800/5"
          >
            Not yet
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="flex-1 rounded-lg bg-felt-700 py-2 text-sm font-medium text-white hover:bg-felt-900 disabled:opacity-50"
          >
            {pending ? 'Publishing…' : 'Publish'}
          </button>
        </>
      }
    >
      <ul className="space-y-2.5 text-sm leading-relaxed text-ink-800/80">
        <li>
          <strong className="text-ink-950">This definition freezes.</strong> A published definition
          can never be edited — not by you, not by anybody. A player asking in two years why they
          scored what they scored gets this exact document as the answer.
        </li>
        <li>
          <strong className="text-ink-950">Running leaderboards are unaffected.</strong> A
          leaderboard pins its version when it starts and keeps it, so publishing cannot rewrite
          standings anybody has already been paid against.
        </li>
        <li>
          <strong className="text-ink-950">Rooms can start using it.</strong> Only published
          versions appear when a leaderboard picks a system; drafts never do.
        </li>
        <li>
          To change it afterwards, use <em>Edit as new version</em> — that copies this into a fresh
          draft and leaves the published one exactly as it is.
        </li>
        {unsavedScope ? (
          <li>
            <strong className="text-ink-950">Your test scope is not part of this.</strong> It has
            unsaved changes, and they are safe: what you test against is stored beside the
            definition rather than inside it, and stays editable after publishing. Save it now or
            afterwards.
          </li>
        ) : null}
      </ul>
    </Dialog>
  );
}

/**
 * The one exit this screen cannot intercept itself. admin-web has no central unsaved-work guard
 * the way room-web does — its editors have all been dialogs until now — so this covers the case
 * that actually loses work: closing the tab on a draft.
 */
function useBeforeUnload(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Ignored by every browser — they show their own wording — so this is only about the
      // prompt appearing at all.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
}
